import React, { RefObject, useState } from "react";

import {
    DownOutlined,
    PushpinFilled,
    PushpinOutlined,
} from "@ant-design/icons";
import { Column, RowData, Table } from "@tanstack/react-table";
import { Checkbox, Dropdown, Space } from "antd";
import { cx } from "class-variance-authority";

import { adjustViewToggleId, PendoProps } from "../../../utils/pendo";
import { LinkButton } from "../../LinkButton";
import { ReorderableList } from "../../ReorderableList/ReorderableList";

import { usePinnedWidthBudget } from "./hooks/usePinnedWidthBudget";
import * as theme from "./TSTAdjustView.css";
import { columnPlainLabel } from "./utils/columnPlainLabel";
import { getAdjustableColumns } from "./utils/getAdjustableColumns";
import { getColumnLayoutActions } from "./utils/getColumnLayoutActions";

interface TSTAdjustViewProps<T extends RowData> extends PendoProps {
    table: Table<T>;
    children: React.ReactNode;
    columnsLabel: string;
    reorderHintLabel: string;
    resetLabel: string;
    pinLabel: string;
    unpinLabel: string;
    disabled?: boolean;
    /** Table scroll container; when set, pinning is bounded to the space left
     * for the scrollable region. */
    containerRef?: RefObject<HTMLElement | null>;
}

export function TSTAdjustView<T extends RowData>({
    table,
    children,
    columnsLabel,
    reorderHintLabel,
    resetLabel,
    pinLabel,
    unpinLabel,
    disabled,
    containerRef,
    "data-pendo": dataPendo,
}: TSTAdjustViewProps<T>) {
    const [opened, setOpened] = useState(false);

    const layout = getAdjustableColumns(table);
    const actions = getColumnLayoutActions(table, layout);
    const { canFitPin } = usePinnedWidthBudget({
        containerRef,
        reattachKey: opened,
        layout,
        onOverflow: actions.unpinColumns,
    });

    const renderItem = (column: Column<T>) => {
        const isPinned = column.getIsPinned() === "left";

        const hasOneUnpinnedColumn = layout.unpinnedAccessorColumnCount <= 1;
        const pinDisabled =
            !isPinned && (hasOneUnpinnedColumn || !canFitPin(column));
        return (
            <AdjustViewItem
                table={table}
                column={column}
                pinLabel={pinLabel}
                unpinLabel={unpinLabel}
                pinDisabled={pinDisabled}
                onTogglePin={() => actions.togglePin(column)}
                onToggleVisibility={actions.toggleVisibility(column)}
            />
        );
    };

    return (
        <Dropdown
            open={opened}
            onOpenChange={setOpened}
            trigger={["click"]}
            placement="bottomRight"
            disabled={disabled}
            dropdownRender={() => (
                <AdjustViewContent
                    columnsLabel={columnsLabel}
                    reorderHintLabel={reorderHintLabel}
                    resetLabel={resetLabel}
                    columns={layout.orderedAccessorColumns}
                    renderItem={renderItem}
                    onReorder={actions.reorder}
                    onReset={actions.reset}
                    canReset={actions.canReset}
                />
            )}
        >
            <a
                data-pendo={dataPendo}
                className="ant-dropdown-link"
                onClick={(e) => {
                    e.preventDefault();
                }}
            >
                <Space>
                    {children}
                    <DownOutlined />
                </Space>
            </a>
        </Dropdown>
    );
}

interface AdjustViewContentProps<T extends RowData> {
    columnsLabel: string;
    reorderHintLabel: string;
    resetLabel: string;
    columns: Array<Column<T>>;
    renderItem: (column: Column<T>) => React.ReactNode;
    onReorder: (reordered: Array<Column<T>>) => void;
    onReset: () => void;
    canReset: boolean;
}

function AdjustViewContent<T extends RowData>({
    columnsLabel,
    reorderHintLabel,
    resetLabel,
    columns,
    renderItem,
    onReorder,
    onReset,
    canReset,
}: AdjustViewContentProps<T>) {
    return (
        <div className={theme.dropdownBody}>
            <div className={theme.header}>
                <div className={theme.headerText}>
                    <span className={theme.headerTitle}>{columnsLabel}</span>
                    <span className={theme.headerSubtitle}>
                        {reorderHintLabel}
                    </span>
                </div>
                <LinkButton onClick={onReset} disabled={!canReset}>
                    {resetLabel}
                </LinkButton>
            </div>
            <ReorderableList
                items={columns}
                getId={(column) => column.id}
                renderItem={renderItem}
                onReorder={onReorder}
                isItemDisabled={(column) => column.getIsPinned() === "left"}
                lockDisabledItems
            />
        </div>
    );
}

interface AdjustViewItemProps<T extends RowData> {
    table: Table<T>;
    column: Column<T>;
    pinLabel: string;
    unpinLabel: string;
    pinDisabled: boolean;
    onTogglePin: () => void;
    onToggleVisibility: (e: React.MouseEvent) => void;
}

function AdjustViewItem<T extends RowData>({
    table,
    column,
    pinLabel,
    unpinLabel,
    pinDisabled,
    onTogglePin,
    onToggleVisibility,
}: AdjustViewItemProps<T>) {
    const headerRendered = columnPlainLabel(column, table);
    const checkboxDisabled = !column.getCanHide();
    const isPinned = column.getIsPinned() === "left";
    const pinTitle = isPinned ? unpinLabel : pinLabel;
    return (
        // Pendo adoption tracking: a stable per-column selector for the
        // visibility toggle row (e.g. "adjust-view-health-toggle").
        <div
            className={theme.itemRow}
            data-pendo={adjustViewToggleId(column.id)}
        >
            <Space className={theme.itemLabel} onClick={onToggleVisibility}>
                <Checkbox
                    checked={column.getIsVisible()}
                    disabled={checkboxDisabled}
                />
                {headerRendered}
            </Space>
            {column.getCanPin() && (
                <button
                    type="button"
                    aria-label={pinTitle}
                    aria-pressed={isPinned}
                    disabled={pinDisabled}
                    className={cx(
                        theme.pinButton,
                        isPinned && theme.pinButtonActive,
                        pinDisabled && theme.pinButtonDisabled
                    )}
                    onClick={(e) => {
                        e.stopPropagation();
                        onTogglePin();
                    }}
                >
                    {isPinned ? <PushpinFilled /> : <PushpinOutlined />}
                </button>
            )}
        </div>
    );
}
